import { useState, useEffect, useRef } from 'react'
import { Link } from 'react-router-dom'
import { motion, AnimatePresence, useInView, useMotionValue, animate } from 'framer-motion'
import { ArrowRight, Globe, Zap, Users, BarChart3, Smartphone, ChevronDown, Phone, Mail, MessageCircle } from 'lucide-react'
import Button from '../components/ui/Button'
import SEO from '../components/SEO'
import { MODULES, TESTIMONIALS } from '../utils/mockData'
import SmsPhoneMockup from '../components/SmsPhoneMockup'
import RecordConverge from '../components/RecordConverge'

const WEBSITE_SCHEMA = {
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  name: 'MySchoolPortal',
  url: 'https://myschoolportal.xyz',
  potentialAction: {
    '@type': 'SearchAction',
    target: {
      '@type': 'EntryPoint',
      urlTemplate: 'https://myschoolportal.xyz/blog?q={search_term_string}',
    },
    'query-input': 'required name=search_term_string',
  },
}

const LANDING_SCHEMA = {
  '@context': 'https://schema.org',
  '@type': 'SoftwareApplication',
  name: 'MySchoolPortal',
  applicationCategory: 'EducationApplication',
  operatingSystem: 'Web, iOS, Android',
  url: 'https://myschoolportal.xyz',
  description: 'Your school, running itself. Feeding, fees, attendance, academics, transport and parent SMS handled from one record.',
  provider: { '@type': 'Organization', name: 'Tvantra', url: 'https://myschoolportal.xyz' },
  featureList: [
    'Feeding management', 'Attendance tracking', 'School fees management',
    'Academic records', 'Parent SMS notifications', 'Transport management',
    'Hostel management', 'Library management', 'Admissions pipeline',
    'Inventory tracking', 'Parent portal',
  ],
  aggregateRating: {
    '@type': 'AggregateRating',
    ratingValue: '4.9',
    reviewCount: '13',
    bestRating: '5',
  },
}

// Shared scroll-reveal used across hero sections below the fold. `once`
// so it doesn't re-fire on scroll-back; delay lets a heading and its
// content stagger slightly instead of popping in together.
const reveal = (delay = 0) => ({
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: '-80px' },
  transition: { duration: 0.5, delay, ease: [0.2, 0.8, 0.2, 1] },
})

/* Related-module ids, used to highlight connections in the diagram. Not
   part of MODULES since it's a display concern of this one component. */
const RELATED = {
  feeding: ['attendance', 'finance'],
  attendance: ['feeding', 'academics'],
  academics: ['attendance', 'parents'],
  finance: ['feeding', 'transport', 'communications'],
  transport: ['finance'],
  hostel: ['inventory'],
  library: ['inventory'],
  admissions: ['academics', 'parents'],
  inventory: ['hostel', 'library'],
  parents: ['academics', 'communications'],
  communications: ['finance', 'parents'],
}

// PLACEHOLDER FIGURES — NOT VERIFIED. These are invented illustrative
// numbers standing in for real outcome data. Replace every value here with
// measured figures (and keep a source for each) before this goes public;
// they read as factual claims about what the product delivers.
const HERO_STATS = [
  { value: 6, decimals: 0, suffix: ' hrs', label: 'admin hours saved', sub: 'per week, per school' },
  { value: 3, decimals: 0, suffix: 'x', label: 'faster fee collection', sub: 'arrears cleared sooner' },
  { value: 95, decimals: 0, suffix: '%', label: 'parents reached instantly', sub: 'SMS on every update' },
]

// Counts from 0 to its target value once the block scrolls into view.
// SSR renders the final value directly (no JS on the server to animate),
// so prerendered/no-JS output still shows the real number, not a stuck 0.
function StatBlock({ value, decimals, prefix = '', suffix, label, sub, delay = 0, first = false }) {
  const ref = useRef(null)
  const inView = useInView(ref, { once: false, margin: '-40px' })
  const motionValue = useMotionValue(0)
  const [display, setDisplay] = useState(value.toFixed(decimals))

  useEffect(() => {
    if (!inView) {
      motionValue.set(0)
      setDisplay((0).toFixed(decimals))
      return
    }
    const controls = animate(motionValue, value, {
      duration: 1.1,
      delay,
      ease: [0.2, 0.8, 0.2, 1],
      onUpdate: (v) => setDisplay(v.toFixed(decimals)),
    })
    return () => controls.stop()
  }, [inView, value, decimals, delay, motionValue])

  return (
    <div ref={ref} className={`flex flex-col px-7 py-4 ${first ? '' : 'border-l border-theme'}`}>
      <span className="font-boxy text-2xl font-bold text-ink">{prefix}{display}{suffix}</span>
      <span className="text-xs font-semibold text-ink mt-1">{label}</span>
      {sub && <span className="text-[11px] text-[var(--ink-soft)] mt-0.5">{sub}</span>}
    </div>
  )
}

// Fixed positions/timings (no Math.random) so server and client render
// identically and hydration doesn't mismatch.
function Floaters({ items }) {
  return (
    <g>
      {items.map((f, i) => (
        <circle
          key={i}
          className="hero-floater"
          cx={f.x}
          cy={f.y}
          r={f.r}
          fill="var(--accent)"
          style={{ animationDuration: `${f.dur}s`, animationDelay: `${f.delay}s` }}
        />
      ))}
    </g>
  )
}

function ConvergeCore({ cx, cy, wordY }) {
  return (
    <>
      <g className="converge-core" style={{ animationDelay: '1.5s' }}>
        <circle className="converge-ring" cx={cx} cy={cy} r="18" />
        <circle cx={cx} cy={cy} r="7" fill="var(--accent-strong)" />
        <circle cx={cx} cy={cy} r="14" fill="none" stroke="var(--accent-strong)" strokeWidth="1.5" opacity="0.4" />
      </g>
      <text className="converge-word" x={cx} y={wordY} textAnchor="middle">One system</text>
    </>
  )
}

/* ── Hero convergence animation: operational threads drawing in and
   resolving into one system, on load. Plays once; respects reduced motion
   (see index.css). Desktop gets the full six-thread diagram; below the md
   breakpoint six labeled threads shrink past legibility, so a separate,
   simpler four-thread SVG with larger relative text renders instead
   (toggled by CSS display, not JS, to stay SSR-safe). ── */
function ConvergeAnimation() {
  const threadsDesktop = [
    { label: 'Feeding',        path: 'M40,60   C 260,60  360,90  480,190', color: 'var(--ink-soft)', delay: 0 },
    { label: 'Attendance',     path: 'M40,140  C 240,140 340,150 480,190', color: 'var(--accent)',    delay: 0.12 },
    { label: 'Finance',        path: 'M40,220  C 240,220 340,210 480,190', color: 'var(--warm-strong)', delay: 0.24 },
    { label: 'Academics',      path: 'M40,300  C 260,300 360,270 480,190', color: 'var(--accent-strong)',  delay: 0.36 },
    { label: 'Transport',      path: 'M920,90  C 700,90  600,120 480,190', color: 'var(--ink-soft)', delay: 0.18, end: true },
    { label: 'Communications', path: 'M920,270 C 700,270 600,230 480,190', color: 'var(--accent)',   delay: 0.3,  end: true },
  ]

  const floatersDesktop = [
    { x: 120, y: 320, r: 2.5, dur: 9,  delay: 0 },
    { x: 300, y: 350, r: 2,   dur: 11, delay: 1.2 },
    { x: 620, y: 330, r: 2.5, dur: 10, delay: 0.6 },
    { x: 800, y: 350, r: 2,   dur: 8,  delay: 2 },
    { x: 480, y: 340, r: 1.8, dur: 12, delay: 1.6 },
    { x: 220, y: 60,  r: 2,   dur: 10, delay: 0.4 },
    { x: 700, y: 40,  r: 2.2, dur: 9,  delay: 2.4 },
    { x: 900, y: 180, r: 1.8, dur: 11, delay: 1 },
  ]

  // Mobile: four threads, tighter viewBox, text scaled up relative to the
  // stage so it stays readable once the SVG is scaled down to phone width.
  const threadsMobile = [
    { label: 'Feeding',    path: 'M20,40  C 140,40  190,80  280,140', color: 'var(--ink-soft)', delay: 0 },
    { label: 'Finance',    path: 'M20,110 C 130,110 180,120 280,140', color: 'var(--warm-strong)', delay: 0.16 },
    { label: 'Attendance', path: 'M540,40  C 420,40  370,80  280,140', color: 'var(--accent)', delay: 0.08, end: true },
    { label: 'Academics',  path: 'M540,110 C 430,110 380,120 280,140', color: 'var(--accent-strong)', delay: 0.24, end: true },
  ]

  const floatersMobile = [
    { x: 90, y: 200, r: 2.2, dur: 9,  delay: 0 },
    { x: 470, y: 210, r: 2,  dur: 10, delay: 1 },
    { x: 280, y: 210, r: 1.8, dur: 11, delay: 0.6 },
  ]

  return (
    <div className="relative w-full mt-16 mb-2" aria-hidden="true">
      {/* Mobile (below md) */}
      <div className="block md:hidden" style={{ height: 'clamp(200px, 60vw, 260px)' }}>
        <svg viewBox="0 0 560 240" preserveAspectRatio="xMidYMid meet" className="w-full h-full" style={{ overflow: 'visible' }}>
          <Floaters items={floatersMobile} />
          <g>
            {threadsMobile.map((t, i) => {
              const [sx, sy] = t.path.match(/M([\d.]+),([\d.]+)/).slice(1).map(Number)
              return (
                <g key={t.label}>
                  <path className="thread-path thread-path-mobile" style={{ animationDelay: `${t.delay}s` }} d={t.path} stroke={t.color} />
                  <circle className="thread-node" style={{ animationDelay: `${0.7 + i * 0.06}s` }} cx={sx} cy={sy} r="5" fill={t.color} />
                  <text
                    className="thread-label thread-label-mobile"
                    style={{ animationDelay: `${0.75 + i * 0.06}s` }}
                    x={t.end ? sx - 10 : sx + 10}
                    y={sy - 8}
                    textAnchor={t.end ? 'end' : 'start'}
                  >
                    {t.label}
                  </text>
                </g>
              )
            })}
          </g>
          <ConvergeCore cx={280} cy={140} wordY={175} />
        </svg>
      </div>

      {/* Desktop (md and up) */}
      <div className="hidden md:block" style={{ height: 'clamp(260px, 34vw, 420px)' }}>
        <svg viewBox="0 0 960 380" preserveAspectRatio="xMidYMid meet" className="w-full h-full" style={{ overflow: 'visible' }}>
          <Floaters items={floatersDesktop} />
          <g>
            {threadsDesktop.map((t, i) => {
              const [sx, sy] = t.path.match(/M([\d.]+),([\d.]+)/).slice(1).map(Number)
              return (
                <g key={t.label}>
                  <path className="thread-path" style={{ animationDelay: `${t.delay}s` }} d={t.path} stroke={t.color} />
                  <circle className="thread-node" style={{ animationDelay: `${0.85 + i * 0.06}s` }} cx={sx} cy={sy} r="4" fill={t.color} />
                  <text
                    className="thread-label"
                    style={{ animationDelay: `${0.9 + i * 0.06}s` }}
                    x={t.end ? sx - 42 : sx + 12}
                    y={sy - 4}
                    textAnchor={t.end ? 'end' : 'start'}
                  >
                    {t.label}
                  </text>
                </g>
              )
            })}
          </g>
          <ConvergeCore cx={480} cy={190} wordY={232} />
        </svg>
      </div>
    </div>
  )
}

// The card's lines arrive one after another rather than as a block, so a
// module change reads as the detail settling in rather than a hard swap.
// The outgoing copy leaves faster than the new copy arrives.
const CARD_VARIANTS = {
  hidden:  { opacity: 0, y: 16, filter: 'blur(4px)' },
  shown:   {
    opacity: 1,
    y: 0,
    filter: 'blur(0px)',
    transition: {
      duration: 1.1,
      ease: [0.16, 0.7, 0.2, 1],
      staggerChildren: 0.16,
      delayChildren: 0.18,
    },
  },
  leaving: { opacity: 0, y: -10, filter: 'blur(4px)', transition: { duration: 0.45, ease: 'easeIn' } },
}

const CARD_LINE = {
  hidden:  { opacity: 0, y: 14, filter: 'blur(5px)' },
  shown:   { opacity: 1, y: 0, filter: 'blur(0px)', transition: { duration: 1.15, ease: [0.16, 0.7, 0.2, 1] } },
  leaving: { opacity: 0, y: -8, transition: { duration: 0.3, ease: 'easeIn' } },
}

/* ── Module diagram: hub-and-spoke, hover highlights related modules ── */
function ModuleDiagram() {
  const [active, setActive] = useState(MODULES[0])
  const [paused, setPaused] = useState(false)
  const wrapRef = useRef(null)
  const CX = 230, CY = 230, R = 175, NW = 118, NH = 40

  // Walk the modules on their own so the diagram tells its story without
  // needing a hover. Pauses while the visitor is driving it, while the
  // diagram is off-screen, and for reduced-motion users.
  useEffect(() => {
    if (paused) return
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return

    let timer = 0
    const advance = () => {
      setActive((current) => {
        const i = MODULES.findIndex((m) => m.id === current.id)
        return MODULES[(i + 1) % MODULES.length]
      })
    }

    const observer = new IntersectionObserver(([entry]) => {
      window.clearInterval(timer)
      if (entry.isIntersecting) timer = window.setInterval(advance, 4500)
    }, { threshold: 0.25 })

    if (wrapRef.current) observer.observe(wrapRef.current)
    return () => {
      window.clearInterval(timer)
      observer.disconnect()
    }
  }, [paused])

  // Math.cos/sin can differ in their last float digit between the SSG
  // server's V8 and the browser's, which trips a React hydration warning
  // on the rendered path/rect coordinates. Rounding keeps both in sync.
  const round = (n) => Math.round(n * 1000) / 1000
  const positions = MODULES.reduce((acc, m, i) => {
    const angle = (Math.PI * 2 * i) / MODULES.length - Math.PI / 2
    acc[m.id] = { x: round(CX + R * Math.cos(angle)), y: round(CY + R * Math.sin(angle)) }
    return acc
  }, {})

  const relatedIds = new Set([active.id, ...(RELATED[active.id] || [])])

  return (
    <div
      ref={wrapRef}
      className="grid md:grid-cols-2 gap-2 items-center"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocusCapture={() => setPaused(true)}
      onBlurCapture={() => setPaused(false)}
    >
      <div>
        <svg viewBox="0 0 460 460" className="w-full h-auto" style={{ overflow: 'visible' }}>
          <g>
            {MODULES.map((m) => {
              const p = positions[m.id]
              const isRelated = relatedIds.has(m.id)
              return (
                <path
                  key={m.id}
                  d={`M${p.x},${p.y} Q ${p.x + (CX - p.x) * 0.5},${p.y + (CY - p.y) * 0.5} ${CX},${CY}`}
                  fill="none"
                  stroke={isRelated ? 'var(--accent)' : 'var(--rule)'}
                  strokeWidth={isRelated ? 2 : 1.5}
                  strokeOpacity={isRelated ? 1 : 0.55}
                  style={{ transition: 'stroke 0.9s ease, stroke-width 0.9s ease, stroke-opacity 0.9s ease' }}
                />
              )
            })}
          </g>
          <g>
            <circle
              cx={CX}
              cy={CY}
              r="30"
              fill="color-mix(in srgb, var(--accent) 16%, var(--bg-base))"
              stroke="var(--accent-strong)"
              strokeWidth="1.5"
            />
            <text x={CX} y={CY + 4} textAnchor="middle" fill="var(--accent-strong)" fontWeight="700" fontSize="11">CORE</text>
          </g>
          <g>
            {MODULES.map((m, i) => {
              const p = positions[m.id]
              const isActive = m.id === active.id
              return (
                <g
                  key={m.id}
                  tabIndex={0}
                  role="button"
                  aria-label={m.name}
                  style={{
                    cursor: 'pointer',
                    transform: isActive ? 'scale(1.06)' : 'scale(1)',
                    transformOrigin: `${p.x}px ${p.y}px`,
                    transition: 'transform 0.9s cubic-bezier(0.16, 0.7, 0.2, 1)',
                  }}
                  onMouseEnter={() => setActive(m)}
                  onFocus={() => setActive(m)}
                >
                  <rect
                    x={p.x - NW / 2}
                    y={p.y - NH / 2}
                    width={NW}
                    height={NH}
                    rx="4"
                    fill={isActive ? 'color-mix(in srgb, var(--accent) 8%, var(--bg-surface))' : 'var(--bg-surface)'}
                    stroke={isActive ? 'var(--accent-strong)' : 'var(--rule)'}
                    strokeWidth="1.5"
                    style={{ transition: 'stroke 0.9s ease, fill 0.9s ease' }}
                  />
                  <text x={p.x - NW / 2 + 8} y={p.y - 5} fontFamily="'Space Mono', monospace" fontSize="9" fill="var(--ink-soft)">
                    {String(i + 1).padStart(2, '0')}
                  </text>
                  <text x={p.x - NW / 2 + 8} y={p.y + 12} fontFamily="Inter, sans-serif" fontSize="11.5" fontWeight="600" fill="var(--ink)">
                    {m.name}
                  </text>
                </g>
              )
            })}
          </g>
        </svg>
        <p className="text-center text-xs text-[var(--ink-soft)] mt-2.5">Hover any module to explore what it connects to.</p>
      </div>

      <div style={{ minHeight: 210 }}>
        <AnimatePresence mode="wait">
          <motion.div
            key={active.id}
            className="bg-surface border border-theme rounded p-7 h-full"
            variants={CARD_VARIANTS}
            initial="hidden"
            animate="shown"
            exit="leaving"
          >
            <motion.span variants={CARD_LINE} className="block font-boxy text-xs text-accent mb-3">
              {String(MODULES.findIndex(m => m.id === active.id) + 1).padStart(2, '0')} / {MODULES.length}
            </motion.span>
            <motion.h3 variants={CARD_LINE} className="text-lg font-display font-bold text-ink mb-2.5">{active.name}</motion.h3>
            <motion.p variants={CARD_LINE} className="text-sm text-[var(--ink-soft)] leading-relaxed mb-4 max-w-md">{active.desc}</motion.p>
            <motion.div variants={CARD_LINE}>
              <Link to={`/features#${active.id}`} className="inline-flex items-center gap-1.5 text-sm font-semibold text-accent">
                View {active.name.toLowerCase()} module <ArrowRight size={14} />
              </Link>
            </motion.div>
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  )
}

export default function Landing() {
  return (
    <div className="overflow-x-hidden">
      <SEO
        title="MySchoolPortal | Every School Operation, One System by Tvantra"
        description="Your school, running itself. Feeding, fees, attendance, academics, transport and parent SMS handled from one record."
        path="/"
        schema={[LANDING_SCHEMA, WEBSITE_SCHEMA]}
      />

      {/* ── HERO ── */}
      <section
        className="relative px-4 pt-16 pb-20 photo-hero"
        style={{ '--photo-hero-src': "url('https://images.unsplash.com/photo-1744809482817-9a9d4fc280af?auto=format&fit=crop&w=1900&q=80')" }}
      >
        <div className="max-w-6xl mx-auto">
          <div className="max-w-2xl mt-6">
            <div>
              <h1 className="text-[2.85rem] sm:text-6xl lg:text-[5.25rem] font-display font-extrabold text-white leading-[0.93] tracking-[-0.042em] mb-5 text-balance">
                What if your school{' '}
                <span className="whitespace-nowrap">ran itself?</span>
              </h1>
              <p className="text-lg max-w-lg mb-8 leading-relaxed" style={{ color: 'rgba(255,255,255,0.82)' }}>
                Feeding, fees, attendance, academics and parent SMS, handled automatically, so your team stops chasing paperwork.
              </p>
              <div className="flex flex-wrap items-center gap-3.5">
                <Link to="/contact">
                  <Button variant="primary" size="lg">Get started free <ArrowRight size={16} /></Button>
                </Link>
                <Link to="/contact">
                  <Button variant="ghost" size="lg">Talk to us</Button>
                </Link>
              </div>

              <div className="hero-stats flex flex-wrap rounded w-fit mt-10">
                {HERO_STATS.map((s, i) => (
                  <StatBlock key={s.label} {...s} delay={i * 0.15} first={i === 0} />
                ))}
              </div>
            </div>

          </div>

          <ConvergeAnimation />

          <div className="flex justify-center mt-2" aria-hidden="true">
            <ChevronDown className="scroll-cue text-[var(--ink-soft)]" size={22} />
          </div>
        </div>
      </section>

      {/* ── MODULES ── */}
      <section className="px-4 py-24 border-t border-theme bg-grid-pattern">
        <div className="max-w-6xl mx-auto">
          <motion.div {...reveal()} className="mb-14">
            <h2 className="text-3xl sm:text-4xl font-display font-bold text-ink mb-3">
              Eleven modules. One connected system.
            </h2>
            <p className="text-[var(--ink-soft)] max-w-xl">
              Turn on what your school needs today. Every module shares the same records, the same permissions and the same reports, so nothing runs in isolation.
            </p>
          </motion.div>

          <motion.div {...reveal(0.1)}>
            <ModuleDiagram />
          </motion.div>

          <div className="text-center mt-10">
            <Link to="/features">
              <Button variant="ghost" size="lg">Explore all features <ArrowRight size={16} /></Button>
            </Link>
          </div>
        </div>
      </section>

      {/* ── RECORDS ── */}
      <section className="px-4 py-24 border-t border-theme bg-grid-pattern">
        <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-12 items-center">
          <motion.div
            initial={{ opacity: 0, x: -24 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: '-80px' }}
            transition={{ duration: 0.5, ease: [0.2, 0.8, 0.2, 1] }}
          >
            <h2 className="text-3xl font-display font-bold text-ink mb-3">Every payment, every class, one record.</h2>
            <p className="text-[var(--ink-soft)] max-w-md">
              Whatever happens at your school gets recorded the moment it happens: a payment, an absence, a new enrollment. Nothing is lost between spreadsheets or notebooks.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 24 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: '-80px' }}
            transition={{ duration: 0.5, delay: 0.1, ease: [0.2, 0.8, 0.2, 1] }}
          >
            <RecordConverge />
          </motion.div>
        </div>
      </section>

      {/* ── HOW IT WORKS ── */}
      <section className="px-4 py-24 border-t border-theme bg-grid-pattern">
        <div className="max-w-6xl mx-auto">
          <motion.div {...reveal()} className="mb-14">
            <h2 className="text-3xl sm:text-4xl font-display font-bold text-ink mb-3">Up and running the same day.</h2>
            <p className="text-[var(--ink-soft)]">No IT department required. We configure your workspace and hand you the keys.</p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-14 items-center">
            <motion.div
              initial={{ opacity: 0, x: -24 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, margin: '-80px' }}
              transition={{ duration: 0.5, ease: [0.2, 0.8, 0.2, 1] }}
              className="rounded-2xl overflow-hidden shadow-card"
            >
              <img
                src="https://images.unsplash.com/photo-1744809495173-217ca4faa8bc?auto=format&fit=crop&w=1200&q=80"
                alt="A student working in an exercise book at her desk"
                className="w-full h-full object-cover"
                loading="lazy"
              />
            </motion.div>

            <div className="flex flex-col gap-8">
              {[
                { icon: <Globe size={22} />, title: 'Sign up', desc: "Choose your plan and share your school's details. It takes a few minutes, and nothing technical is required." },
                { icon: <Zap size={22} />, title: 'We configure', desc: 'We set up your workspace, modules and staff accounts, fully ready before you log in.' },
                { icon: <Users size={22} />, title: "You're in", desc: 'Credentials arrive by email. Log in and start running your school operations.' },
              ].map((s, i) => (
                <motion.div
                  key={s.title}
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: i * 0.08 }}
                  className="flex gap-4"
                >
                  <div className="w-9 h-9 rounded-full border flex items-center justify-center font-boxy text-xs text-accent flex-shrink-0 bg-surface" style={{ borderColor: 'var(--accent)' }}>
                    {String(i + 1).padStart(2, '0')}
                  </div>
                  <div>
                    <h3 className="text-base font-display font-bold text-ink mb-2">{s.title}</h3>
                    <p className="text-sm text-[var(--ink-soft)] leading-relaxed">{s.desc}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── RELIABILITY ── */}
      <section className="px-4 py-24" style={{ background: 'var(--panel-dark)' }}>
        <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-12 items-center">
          <motion.div
            initial={{ opacity: 0, x: -24 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: '-80px' }}
            transition={{ duration: 0.5, ease: [0.2, 0.8, 0.2, 1] }}
          >
            <h2 className="text-3xl font-display font-bold text-white mb-3">Built to be trusted with school records.</h2>
            <p className="max-w-md" style={{ color: 'var(--panel-dark-soft)' }}>
              Every action is logged. Every record is backed up. The platform runs the same whether you're on a desktop in the office or a phone in the classroom.
            </p>

            <div className="flex flex-col mt-6">
              {[
                { icon: <BarChart3 size={18} />, title: 'Activity logging', desc: 'Every important action is tracked and attributable.' },
                { icon: <Smartphone size={18} />, title: 'Mobile-friendly', desc: 'Works on phones, tablets and desktops without a separate app.' },
              ].map((f, i) => (
                <div key={f.title} className="flex gap-3.5 py-4" style={{ borderTop: i > 0 ? '1px solid rgba(255,255,255,0.08)' : 'none' }}>
                  <div className="w-6 h-6 rounded flex items-center justify-center flex-shrink-0" style={{ background: 'color-mix(in srgb, var(--accent-strong) 18%, transparent)', color: 'var(--accent-strong)' }}>
                    {f.icon}
                  </div>
                  <div>
                    <h4 className="text-sm font-bold text-white mb-0.5">{f.title}</h4>
                    <p className="text-sm" style={{ color: 'var(--panel-dark-soft)' }}>{f.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 24 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: '-80px' }}
            transition={{ duration: 0.5, delay: 0.1, ease: [0.2, 0.8, 0.2, 1] }}
          >
            <SmsPhoneMockup />
          </motion.div>
        </div>
      </section>

      {/* ── TESTIMONIALS ── */}
      <section className="px-4 py-24 border-t border-theme bg-grid-pattern">
        <div className="max-w-6xl mx-auto">
          <motion.div {...reveal()} className="mb-14">
            <h2 className="text-3xl sm:text-4xl font-display font-bold text-ink mb-3">What changes once it's in place.</h2>
            <p className="text-[var(--ink-soft)]">Three schools, three roles, the same result: less time on paperwork.</p>
          </motion.div>

          <motion.div {...reveal(0.1)} className="rounded overflow-hidden shadow-card mb-10" style={{ maxHeight: 320 }}>
            <img
              src="https://images.unsplash.com/photo-1744809482817-9a9d4fc280af?auto=format&fit=crop&w=1600&q=80"
              alt="A teacher at the board with students in a classroom"
              className="w-full h-full object-cover"
              loading="lazy"
              width={1600}
              height={320}
              style={{ maxHeight: 320, objectPosition: 'center 40%' }}
            />
          </motion.div>

          <div className="grid md:grid-cols-3 gap-px bg-[var(--rule-soft)] border border-theme rounded overflow-hidden">
            {TESTIMONIALS.map((t, i) => (
              <motion.div
                key={t.school}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-80px' }}
                transition={{ duration: 0.4, delay: i * 0.1 }}
                className="bg-surface p-6"
              >
                <p className="font-boxy text-[10.5px] uppercase tracking-wide text-accent mb-3.5">{t.school}</p>
                <p className="text-sm text-ink leading-relaxed mb-4">&ldquo;{t.quote}&rdquo;</p>
                <p className="text-sm font-bold text-ink">{t.name}</p>
                <p className="text-xs text-[var(--ink-soft)]">{t.role}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="px-4 py-24">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, scale: 0.97 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true, margin: '-80px' }}
            transition={{ duration: 0.5, ease: [0.2, 0.8, 0.2, 1] }}
            className="rounded p-10 sm:p-14 grid sm:grid-cols-[1fr_auto] gap-8 items-center"
            style={{ background: 'var(--panel-dark)' }}
          >
            <div>
              <h2 className="text-3xl sm:text-4xl font-display font-bold text-white mb-2.5">Ready to run your school on one system?</h2>
              <p className="max-w-md" style={{ color: 'var(--panel-dark-soft)' }}>Join the schools already using MySchoolPortal to streamline operations and communication.</p>
            </div>
            <div className="flex flex-col items-start gap-3">
              <div className="flex flex-wrap gap-3">
                <Link to="/contact">
                  <Button variant="primary" size="lg" className="!bg-[var(--accent-strong)] hover:!bg-[var(--accent)]">Start free trial</Button>
                </Link>
                <Link to="/contact">
                  <Button variant="ghost" size="lg" className="!border-white/25 !text-white hover:!border-white/50">Talk to us</Button>
                </Link>
              </div>
            </div>
          </motion.div>

          <div className="grid sm:grid-cols-3 gap-px mt-px rounded overflow-hidden" style={{ background: 'var(--panel-dark-raised)' }}>
            {[
              { icon: <Phone size={16} />, label: 'Call us', value: '+233 59 653 9060', href: 'tel:+233596539060' },
              { icon: <MessageCircle size={16} />, label: 'WhatsApp', value: 'Message us now', href: 'https://wa.me/233596539060?text=' + encodeURIComponent("Hello! I'm interested in MySchoolPortal. Can you tell me more?") },
              { icon: <Mail size={16} />, label: 'Email', value: 'info@myschoolportal.xyz', href: 'mailto:info@myschoolportal.xyz' },
            ].map((c) => (
              <a
                key={c.label}
                href={c.href}
                {...(c.href.startsWith('http') ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
                className="flex items-center gap-3.5 px-6 py-5 transition-colors duration-150 group/contact"
                style={{ background: 'var(--panel-dark)' }}
              >
                <span
                  className="w-9 h-9 rounded flex items-center justify-center flex-shrink-0 transition-colors duration-150"
                  style={{ background: 'color-mix(in srgb, var(--accent) 16%, transparent)', color: 'var(--accent-strong)' }}
                >
                  {c.icon}
                </span>
                <span className="min-w-0">
                  <span className="block font-boxy text-[10.5px] uppercase tracking-wide" style={{ color: 'var(--panel-dark-mute)' }}>{c.label}</span>
                  <span className="block text-sm font-semibold text-white truncate group-hover/contact:text-[var(--accent-strong)] transition-colors duration-150">{c.value}</span>
                </span>
              </a>
            ))}
          </div>
        </div>
      </section>
    </div>
  )
}
